import * as React from "react";
import {useMemo, useState} from "react";
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {IRetentionRow} from "../../../../api/AdminAnalyticsApi";
import {applySort, SortDir} from "../../../../utils/listUtil";
import {sortTh} from "../shared/SortableTh.component";
import {
    axisTickStyle,
    chartColors,
    gridProps,
    gridStroke,
    RETENTION_DESCRIPTION,
    tooltipStyle,
} from "../shared/analytics.constants";
import {numberWithCommas} from "../../../../utils/numberUtil";

interface IRetentionSectionProps {
    retention: IRetentionRow[];
}

export const RetentionSection: React.FC<IRetentionSectionProps> = ({retention}) => {
    const [retentionSort, setRetentionSort] = useState<{ col: string; dir: SortDir }>({col: "period", dir: "desc"});
    const sortedRetention = useMemo(
        () => applySort(retention, retentionSort.col, retentionSort.dir),
        [retention, retentionSort]);

    const avgReturnRate = retention.length > 0
        ? Math.round(retention.reduce((sum, r) => sum + r.retentionPercent, 0) / retention.length)
        : 0;

    return (
        <section className="admin-analytics__section" data-testid={"retention"}>
            <div className="admin-analytics__section-header">
                <h2 className="admin-analytics__section-title">User Retention</h2>
                <span className="admin-analytics__section-subtitle">
                    Quarterly return rate &mdash; are users coming back?
                </span>
            </div>
            <p className="admin-analytics__section-desc">{RETENTION_DESCRIPTION}</p>
            <div className="admin-analytics__summary-grid">
                <div className="admin-analytics__summary-card admin-analytics__summary-card--total">
                    <div className="admin-analytics__summary-label">Latest Return Rate</div>
                    <div className="admin-analytics__summary-value">
                        {retention[retention.length - 1].retentionPercent}%
                    </div>
                    <div className="admin-analytics__summary-note">of prior-quarter users returned</div>
                </div>
                <div className="admin-analytics__summary-card admin-analytics__summary-card--internal">
                    <div className="admin-analytics__summary-label">Avg Return Rate</div>
                    <div className="admin-analytics__summary-value">{avgReturnRate}%</div>
                    <div className="admin-analytics__summary-note">across all quarters</div>
                </div>
            </div>
            <h3 className="admin-analytics__subsection-title">Retention Rate by Quarter</h3>
            <div className="admin-analytics__chart">
                <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={retention}>
                        <defs>
                            <linearGradient id="retentionFill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={chartColors.tealDark} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={chartColors.tealDark} stopOpacity={0.05}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="period" tick={axisTickStyle} axisLine={{stroke: gridStroke}}
                               tickLine={false}/>
                        <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v}%`}
                               tick={axisTickStyle} axisLine={false} tickLine={false}/>
                        <Tooltip formatter={(value) => [`${value}%`, "Retention"]} {...tooltipStyle} />
                        <Area type="monotone" dataKey="retentionPercent" stroke={chartColors.tealDark}
                              fill="url(#retentionFill)" strokeWidth={2} name="Retention %"/>
                    </AreaChart>
                </ResponsiveContainer>
            </div>
            <table className="admin-analytics__table">
                <thead>
                <tr>
                    {sortTh("period", "Quarter", retentionSort, setRetentionSort, undefined, "desc")}
                    {sortTh("priorUsers", "Prior Quarter Users", retentionSort, setRetentionSort,
                        "admin-analytics__cell--right")}
                    {sortTh("retainedUsers", "Returned", retentionSort, setRetentionSort,
                        "admin-analytics__cell--right")}
                    {sortTh("retentionPercent", "Retention %", retentionSort, setRetentionSort,
                        "admin-analytics__cell--right")}
                </tr>
                </thead>
                <tbody>{sortedRetention.map((row) => (
                    <tr key={row.period}>
                        <td>{row.period}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.priorUsers)}</td>
                        <td className="admin-analytics__cell--right">{numberWithCommas(row.retainedUsers)}</td>
                        <td className="admin-analytics__cell--right">{row.retentionPercent}%</td>
                    </tr>
                ))}</tbody>
            </table>
        </section>
    );
};
